import { DateTime } from "luxon";
import { datetime, RRule, type Options } from "rrule";

import { MeetingSlot } from "../../store";

export type RecurringTrackerMeetingSeed = {
  title?: string;
  rrule?: string | null;
  dtstart?: string | null;
  durationMinutes?: number | null;
};

const getRRuleDate = (date: DateTime) => (
  datetime(date.year, date.month, date.day, date.hour, date.minute, date.second)
);

const getZonedDateFromRRuleOccurrence = (occurrence: Date, time: DateTime, timezone: string) => {
  const occurrenceDate = DateTime.fromJSDate(occurrence, { zone: "UTC" });
  return DateTime.fromObject(
    {
      year: occurrenceDate.year,
      month: occurrenceDate.month,
      day: occurrenceDate.day,
      hour: time.hour,
      minute: time.minute,
      second: time.second,
      millisecond: 0,
    },
    { zone: timezone }
  );
};

const getRRuleOptions = (rrule: string): Partial<Options> | null => {
  try {
    return RRule.parseString(rrule.replace(/^RRULE:/i, ""));
  } catch {
    return null;
  }
};

const dropPastUntil = (options: Partial<Options>, today: DateTime) => {
  const nextOptions = { ...options };
  if (nextOptions.until && DateTime.fromJSDate(nextOptions.until, { zone: "UTC" }) < today) {
    delete nextOptions.until;
  }
  return nextOptions;
};

const getRecurrenceDescription = (rrule: string) => {
  const options = getRRuleOptions(rrule);
  if (!options) return "recurring";
  return new RRule(options).toText();
};

export const getRecurringTrackerAlertMessage = (
  seed: RecurringTrackerMeetingSeed,
) => (
  `Meeting copied from ${seed.title || "the previous event series"}. `
  + `In the last series, event was ${seed.rrule ? getRecurrenceDescription(seed.rrule) : "recurring"}.`
);

export const getRecurringTrackerSeedSlots = (
  seed: RecurringTrackerMeetingSeed | undefined,
  meetingId: number,
  fallbackTitle: string,
  timezone: string,
): MeetingSlot[] => {
  if (!seed?.rrule || !seed.dtstart) return [];

  const originalOptions = getRRuleOptions(seed.rrule);
  const originalStart = DateTime.fromISO(seed.dtstart).setZone(timezone);
  if (!originalOptions || !originalStart.isValid) return [];

  const today = DateTime.now().setZone(timezone).startOf("day");
  const options = dropPastUntil(originalOptions, today);
  const occurrenceRule = new RRule({
    ...options,
    dtstart: getRRuleDate(originalStart),
  });
  const occurrence = occurrenceRule.after(getRRuleDate(today), true);
  if (!occurrence) return [];

  const start = getZonedDateFromRRuleOccurrence(occurrence, originalStart, timezone);
  if (!start.isValid) return [];

  const durationMinutes = seed.durationMinutes || 30;
  const end = start.plus({ minutes: durationMinutes });

  return [{
    id: -100000,
    meeting: meetingId,
    start_date: start.toISO() || "",
    end_date: end.toISO() || "",
    is_exclude: false,
    all_day: false,
    recurrence_rule: {
      rrule: RRule.optionsToString(options),
    },
    title: seed.title || fallbackTitle,
  }];
};
